import { useState } from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { differenceInMilliseconds } from 'date-fns'
import { Image } from 'expo-image'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SafeAreaView } from 'react-native-safe-area-context'

import { GET_BONUS_ACTIVITY_CONNECTION } from '@/constants/graphql/queries'
import { formatDate } from '@/utils/formatter'
import { Tag } from '@/components/base'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'

export default function ActivityScreen() {
  const { t } = useTranslation()
  const id = useLocalSearchParams().id

  const { data, loading, error } = useQuery(GET_BONUS_ACTIVITY_CONNECTION, {
    variables: {
      activityId: id
    }
  })

  if (loading) {
    return <LoadingScreen />
  }

  if (error || !data?.getBonusActivityConnection?.edges?.length) {
    return <NotFoundScreen />
  }

  const activity = data.getBonusActivityConnection.edges[0].node
  const { title, description, picturesBucketPath, startAt, endedAt, targetVipLevel } = activity
  const status = getActivityStatus(startAt, endedAt)
  const sortedTargetVipLevels = [...(targetVipLevel ?? [])].sort((a: number, b: number) => a - b)

  return (
    <>
      <Stack.Screen options={{ header: () => <NavigationBar>{t('activity:details')}</NavigationBar> }} />
      <SafeAreaView className={'flex-1 bg-background-primary'}>
        <ScrollView>
          <View className={'gap-3 p-4'}>
            {picturesBucketPath?.map((image: string, index: number) => (
              <ActivityImage uri={image} key={index} />
            ))}
            <Text className={'text-2xl font-semibold text-text-primary'}>{title}</Text>
            <View className={'w-fit'}>
              <Tag mood={getStatusTagColor(status)}>{t(`activity:status.${status}`)}</Tag>
            </View>
            <View>
              <Text className={'text-sm text-text-primary'}>{t('activity:startDate', { date: formatDate(startAt) })}</Text>
              <Text className={'text-sm text-text-primary'}>{t('activity:endDate', { date: formatDate(endedAt) })}</Text>
              <Text className={'text-sm text-text-primary'}>{t('activity:rewardTarget', { levels: sortedTargetVipLevels.join(', ') })}</Text>
            </View>
            <Text className={'mb-8 text-sm text-text-primary'}>{description}</Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  )
}

function ActivityImage({ uri }: { uri: string }) {
  const { t } = useTranslation()
  const [aspectRatio, setAspectRatio] = useState(16 / 9)

  return (
    <Image
      accessibilityLabel={t('activity:image')}
      contentFit={'contain'}
      onLoad={({ source }) => {
        if (source.width > 0 && source.height > 0) setAspectRatio(source.width / source.height)
      }}
      source={{ uri }}
      style={{ width: '100%', aspectRatio }}
    />
  )
}

function getActivityStatus(startAt: string, endedAt: string) {
  const isEndTimeEarlierThanStartTime = differenceInMilliseconds(new Date(startAt), new Date(endedAt)) > 0
  const hasBonusActivityStarted = differenceInMilliseconds(new Date(), new Date(startAt)) > 0
  const hasBonusActivityEnded = differenceInMilliseconds(new Date(), new Date(endedAt)) > 0

  if (isEndTimeEarlierThanStartTime) {
    return 'ended'
  }
  if (!hasBonusActivityStarted) {
    return 'has-not-started'
  }
  if (hasBonusActivityEnded) {
    return 'ended'
  }
  return 'ongoing'
}

function getStatusTagColor(status: string) {
  switch (status) {
    case 'ongoing':
      return 'success'
    case 'ended':
      return 'error'
    default:
      return 'neutral'
  }
}
